import { Component } from 'react';
import lodash from 'lodash';
import { connect } from 'react-redux';
import {
    binderInstitutionIdSelector,
    getTimezoneOffset,
} from 'common/selectors';
import { EventHistoryPanel } from './EventHistory';
import {
    EventDetailsPanel,
} from 'pages/events/components/EventDetailsPanel';
import { LoadingStatus } from 'components/common/LoadingStatus/LoadingStatus';
import {
    clearActiveEvent,
    clearEvents,
    createEvent,
    createCustomEvent,
    getEventHistory,
    loadActiveEvent,
    loadActiveCustomEvent,
    loadBinderEvents,
    updateEvent,
    updateCustomEvent,
    deleteEvent,
} from '../actions/events';

import DeleteEventDialog from '../components/deleteEventDialog';

class EventPanel extends Component {
    constructor(props) {
        super(props);
        this.state = {
            activeEvent: {},
        };
    }

    componentDidMount() {
        this.loadSelectedEvent();
    }

    loadSelectedEvent() {
        const {
            selectedEventId,
            loadActiveEvent,
            loadActiveCustomEvent,
            getEventHistory,
            institutionId,
        } = this.props;
        if (selectedEventId !== '') {
            const event = this.props.studyEvents.find(event => event.id === selectedEventId);
            const eventType = event.eventType.name || event.eventType;
            if (eventType === 'Monitor Visit' || eventType === 'ICF Version Change') {
                loadActiveEvent(selectedEventId);
            } else {
                loadActiveCustomEvent(selectedEventId, institutionId);
            }
            getEventHistory(selectedEventId);
        }
    }

    componentDidUpdate(prevProps) {
        const { activeEvent } = this.state;

        if (!lodash.isEqual(activeEvent, this.props.activeEvent)) {
            this.setState({
                activeEvent: this.props.activeEvent,
            });
        }

        if (this.props.selectedEventId !== prevProps.selectedEventId) {
            this.loadSelectedEvent();
        }
    }

    addMonitorDetails(details) {
        const { monitorId } = details;
        const { monitors } = this.props;
        if (monitorId) {
            const fullMonitorModel = lodash.find(monitors, ['id', monitorId]);
            return {
                ...details,
                monitorId,
                monitorName: fullMonitorModel.displayName,
                monitorEmailAddress: fullMonitorModel.emailAddress
            };
        }
        return details;
    }

    formatSignatureRequests(details) {
        const { signatureRequests = [] } = details;

        const formattedSignatureRequests = signatureRequests.reduce((acc, { value, label }) => {
            if (lodash.isNil(value)) {
                return acc;
            }
            return acc.concat({ requestedFrom: value, requestedFromName: label });
        }, []);

        return {
            ...details,
            signatureRequests: formattedSignatureRequests,
        };
    }

    submitEvent(eventType, details) {
        const { activeEvent } = this.state;
        const createMode = lodash.isEmpty(activeEvent);
        const eventDetails = lodash.omit(details, ['eventType', 'signatureRequests', 'signatureRequestsToCancel']);
        const detailsWithMonitors = this.addMonitorDetails(details);
        const fullDetails = this.formatSignatureRequests(detailsWithMonitors);
        const isStaticEvent = ['monitorVisit', 'icfVersionChange'].includes(details.eventType.name);
        if (createMode) {
            this.createEvent(eventType, fullDetails, isStaticEvent, eventDetails);
        } else {
            this.updateEvent(eventType, fullDetails, isStaticEvent);
        }
        this.clearAndClosePanel();
    }

    clearAndClosePanel() {
        this.props.setView('details');
        this.props.clearActiveEvent();
        this.props.closePanel(true);
    }

    cancelEvent() {
        this.clearAndClosePanel();
    }

    updateEvent(eventType, details, isStaticEvent) {
        const {
            updateEvent,
            updateCustomEvent,
            institutionId,
            binderId,
        } = this.props;

        if (isStaticEvent) {
            updateEvent(eventType, details);
        } else {
            updateCustomEvent(institutionId, binderId, eventType, details);
        }
    }

    createEvent(eventType, details, isStaticEvent, eventDetails) {
        const {
            institutionId,
            binderId,
            createEvent,
            createCustomEvent,
        } = this.props;

        const eventData = {
            ...details,
            institutionId,
            binderId,
        };


        if (isStaticEvent) {
            createEvent(eventType, eventData);
        } else {
            createCustomEvent(eventType, eventData, eventDetails);
        }
    }

    handleDeleteEvent() {
        const { deleteEvent } = this.props;
        deleteEvent();
        this.clearAndClosePanel();
    }

    render() {
        const {
            activeEvent,
            activeEventLoading,
            eventHistory,
            eventTypes,
            internalUsers,
            monitors,
            selectedEventId,
            setView,
            timezoneOffset,
            icfLanguages,
            view,
        } = this.props;

        const updateMode = selectedEventId !== '';

        if (activeEventLoading) {
            return <LoadingStatus loading={activeEventLoading} />;
        }

        if(view === 'delete') {
            return <DeleteEventDialog onCancel={this.cancelEvent.bind(this)} onDelete={this.handleDeleteEvent.bind(this)}  />;
        }

        const detailsPanel = <EventDetailsPanel
            monitors={monitors}
            icfLanguages={icfLanguages}
            internalUsers={internalUsers}
            updateMode={updateMode}
            activeEvent={activeEvent}
            eventTypes={eventTypes}
            enabledEventTypes={eventTypes.filter(et => et.isEnabled)}
            eventHistory={eventHistory}
            onSubmit={this.submitEvent.bind(this)}
            onCancel={this.cancelEvent.bind(this)}
            onDelete={() => setView('delete')}
            showHistory={setView.bind(this, 'history')}
            activeEventType={this.props.activeEventType}
            hasManageEvents={this.props.hasManageEvents}
        />;

        const historyPanel = <EventHistoryPanel
            eventHistory={eventHistory}
            showDetails={setView.bind(this, 'details')}
            timezoneOffset={timezoneOffset} />;

        const displayPanel = view === 'details' ? detailsPanel : historyPanel;

        return displayPanel;
    }
}

export default connect(
    state => {
        const activeEvent = lodash.get(state, 'studyEvents.eventData.activeEvent', {});
        const activeEventType = lodash.get(state, 'studyEvents.eventData.activeEventType', {});
        const activeEventLoading = lodash.get(state, 'studyEvents.activeEventLoading', false);
        const binderId = lodash.get(state, 'binder.id');
        const studyId = lodash.get(state, 'study.id');
        const eventHistory = lodash.get(state, 'studyEvents.eventData.activeEventHistory', []);
        const eventTypes = lodash.get(state, 'studyEvents.referenceData.eventTypes', []);
        // these event types only contain id/name/displayName info
        // if a user selects a custom event type, we need to query for it
        const institutionId = binderInstitutionIdSelector(state);
        const internalUsers = lodash.get(state, 'studyEvents.referenceData.internalUsers', []);
        const monitors = lodash.get(state, 'studyEvents.referenceData.monitors', []);
        const selectedEventId = lodash.get(state, 'studyEvents.selectedEventId', '');
        const timezoneOffset = getTimezoneOffset(state);
        const studyEvents = lodash.get(state, 'studyEvents.eventData.list', []);
        return {
            activeEvent,
            activeEventType,
            activeEventLoading,
            binderId,
            studyId,
            eventHistory,
            eventTypes,
            institutionId,
            internalUsers,
            monitors,
            selectedEventId,
            timezoneOffset,
            studyEvents,
        };
    },
    {
        clearActiveEvent,
        clearEvents,
        createEvent,
        deleteEvent,
        createCustomEvent,
        getEventHistory,
        loadActiveEvent,
        loadActiveCustomEvent,
        loadBinderEvents,
        updateEvent,
        updateCustomEvent,
    }
)(EventPanel);

